
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiSearch,
  FiDownload,
  FiRefreshCw,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getSelfActivationTransferTransactions,
} from "../../store/slices/profile";

const SelfActivationWalletTransaction = () => {
  const dispatch = useDispatch();

  const [transactions, setTransactions] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    limit: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState("");

  // Debounce search.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Normalize the API response.
  const normalizeResponse = (response) => {
    const root = response?.data ?? response;
    const data = root?.data ?? root;

    return {
      transactions: Array.isArray(data?.transactions)
        ? data.transactions
        : [],
      totalAmount: Number(data?.totalAmount ?? 0),
      totalTransactions: Number(data?.totalTransactions ?? 0),
      pagination: data?.pagination ?? {},
    };
  };

  // Fetch admin transfer transactions.
  const fetchTransactions = useCallback(
    async ({ requestedPage = page, requestedLimit = limit, requestedSearch = search } = {}) => {
      setLoading(true);
      setError("");

      try {
        const response = await dispatch(
          getSelfActivationTransferTransactions({
            page: requestedPage,
            limit: requestedLimit,
            search: requestedSearch,
          })
        ).unwrap();

        const result = normalizeResponse(response);

        setTransactions(result.transactions);
        setTotalAmount(result.totalAmount);
        setTotalTransactions(result.totalTransactions);
        setPagination({
          total: Number(result.pagination?.total ?? result.totalTransactions),
          currentPage: Number(
            result.pagination?.currentPage ?? requestedPage
          ),
          limit: Number(result.pagination?.limit ?? requestedLimit),
          totalPages: Number(
            result.pagination?.totalPages ??
            Math.ceil(result.totalTransactions / requestedLimit)
          ),
          hasNextPage: Boolean(
            result.pagination?.hasNextPage ??
            requestedPage <
            Math.ceil(result.totalTransactions / requestedLimit)
          ),
          hasPreviousPage: Boolean(
            result.pagination?.hasPreviousPage ?? requestedPage > 1
          ),
        });
      } catch (err) {
        console.error(
          "Failed to fetch self-activation transfer transactions:",
          err
        );

        setTransactions([]);
        setTotalAmount(0);
        setTotalTransactions(0);
        setPagination({
          total: 0,
          currentPage: requestedPage,
          limit: requestedLimit,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        });

        setError(
          typeof err === "string"
            ? err
            : err?.message ||
            err?.data?.message ||
            "Failed to fetch transfer transactions."
        );
      } finally {
        setLoading(false);
      }
    },
    [dispatch, page, limit, search]
  );

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatAmount = (amount) =>
    Number(amount ?? 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "-";

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages =
    pagination.totalPages || Math.ceil(totalTransactions / limit);

  const displayStart =
    totalTransactions > 0 ? (page - 1) * limit + 1 : 0;

  const displayEnd = Math.min(page * limit, totalTransactions);

  const handlePrevious = () => {
    if (page > 1 && !loading) {
      setPage((previous) => previous - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages && !loading) {
      setPage((previous) => previous + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page &&
      !loading
    ) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        page - 1,
        page,
        page + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  // Fetch every matching record for the PDF, independently of the current page.
  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const response = await dispatch(
        getSelfActivationTransferTransactions({
          page: 1,
          limit: Math.max(totalTransactions, 1),
          search,
        })
      ).unwrap();

      const result = normalizeResponse(response);
      const exportList = result.transactions;

      if (exportList.length === 0) {
        window.alert("No transactions available to download.");
        return;
      }

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(16);
      doc.text("Self-Activation Transfer Transactions", 14, 15);

      doc.setFontSize(9);
      doc.text(
        `Records exported: ${exportList.length}`,
        14,
        21
      );
      doc.text(
        `Total transfer amount: ${formatAmount(result.totalAmount)}`,
        14,
        26
      );

      const tableData = exportList.map((item, index) => {
        const user =
          item.userId && typeof item.userId === "object"
            ? item.userId
            : {};

        return [
          index + 1,
          user.name || "-",
          user.userId || "-",
          item.referenceId || "-",
          formatAmount(item.amount),
          formatDate(item.createdAt),
          item.status || "-",
          item.remarks || "-",
        ];
      });

      autoTable(doc, {
        startY: 31,
        head: [
          [
            "#",
            "Name",
            "User ID",
            "Reference ID",
            "Amount",
            "Date",
            "Status",
            "Remarks",
          ],
        ],
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak",
        },
        headStyles: {
          fontSize: 7,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 9 },
          1: { cellWidth: 30 },
          2: { cellWidth: 24 },
          3: { cellWidth: 32 },
          4: { cellWidth: 20 },
          5: { cellWidth: 35 },
          6: { cellWidth: 20 },
          7: { cellWidth: "auto" },
        },
      });

      doc.save("self-activation-transfer-transactions.pdf");
    } catch (err) {
      console.error("PDF download failed:", err);

      window.alert(
        typeof err === "string"
          ? err
          : err?.message || "Failed to download PDF. Please try again."
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchTransactions({
      requestedPage: page,
      requestedLimit: limit,
      requestedSearch: search,
    });
  };

  return (
    <div className="gi-referral-page">
      {/* Breadcrumb */}
      <div className="gi-breadcrumb">
        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>Self-Activation Transfers</span>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>Transaction History</span>
      </div>

      <section className="gi-referral-table-card">
        {/* Filters */}
        <div className="gi-users-filter">
          <div className="gi-users-page-size me-2">
            <label htmlFor="pageSize">Show</label>

            <select
              id="pageSize"
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              disabled={loading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>per page</span>
          </div>

          <div className="gi-users-search me-2">
            <FiSearch />

            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, user ID or reference ID..."
            />
          </div>

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            <FiRefreshCw />

            <span>{loading ? "Loading..." : "Refresh"}</span>
          </button>

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={downloadLoading || loading || totalTransactions === 0}
          >
            <FiDownload />

            <span>
              {downloadLoading ? "Downloading..." : "Download PDF"}
            </span>
          </button>
        </div>

        {/* Summary */}
        <div className="d-flex flex-wrap gap-3 p-3">
          <div className="p-3 border rounded">
            <div className="text-muted">Total Transfer Amount</div>
            <strong>{formatAmount(totalAmount)}</strong>
          </div>

          <div className="p-3 border rounded">
            <div className="text-muted">Total Transactions</div>
            <strong>{totalTransactions.toLocaleString("en-IN")}</strong>
          </div>
        </div>

        {/* Table */}
        <div className="gi-referral-table-wrapper">
          <table className="gi-referral-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th className="text-center">User ID</th>
                <th className="text-center">Reference ID</th>
                <th className="text-center">Amount</th>
                <th className="text-center">Date</th>
                <th className="text-center">Status</th>
                <th>Remarks</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="gi-table-message">
                    Loading self-activation transfer transactions...
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan={8} className="gi-table-message">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && transactions.length === 0 && (
                <tr>
                  <td colSpan={8} className="gi-table-message">
                    No self-activation transfer transactions found.
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                transactions.map((item, index) => {
                  const user =
                    item.userId && typeof item.userId === "object"
                      ? item.userId
                      : {};

                  const status = item.status || "-";

                  return (
                    <tr key={item._id || index}>
                      <td>
                        {String((page - 1) * limit + index + 1).padStart(
                          2,
                          "0"
                        )}
                      </td>

                      <td>{user.name || "-"}</td>

                      <td className="text-center">
                        {user.userId || "-"}
                      </td>

                      <td className="text-center">
                        {item.referenceId || "-"}
                      </td>

                      <td className="text-center">
                        <strong className="gi-payment-amount">
                          {formatAmount(item.amount)}
                        </strong>
                      </td>

                      <td className="text-center">
                        <span className="gi-date-text">
                          {formatDate(item.createdAt)}
                        </span>
                      </td>

                      <td className="text-center">
                        <span
                          className={`gi-status-badge ${String(
                            status
                          ).toLowerCase()}`}
                        >
                          {status}
                        </span>
                      </td>

                      <td>{item.remarks || "-"}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalTransactions > 0 && (
            <div className="gi-users-pagination">
              <div className="gi-pagination-info">
                Showing <strong>{displayStart}</strong> to{" "}
                <strong>{displayEnd}</strong> of{" "}
                <strong>{totalTransactions}</strong> transactions
              </div>

              <div className="gi-pagination-buttons">
                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handlePrevious}
                  disabled={page === 1 || loading}
                  aria-label="Previous page"
                >
                  <FiChevronLeft />
                </button>

                {getPageNumbers().map((pageNumber, index) =>
                  pageNumber === "..." ? (
                    <span
                      key={`dots-${index}`}
                      className="gi-pagination-dots"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={pageNumber}
                      className={`gi-pagination-number ${page === pageNumber ? "active" : ""
                        }`}
                      onClick={() => handlePageChange(pageNumber)}
                      disabled={loading}
                    >
                      {pageNumber}
                    </button>
                  )
                )}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handleNext}
                  disabled={page >= totalPages || loading}
                  aria-label="Next page"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SelfActivationWalletTransaction;
